import {
	Alert,
	AlertIcon,
	Badge,
	Box,
	Button,
	Code,
	Flex,
	FormControl,
	FormHelperText,
	FormLabel,
	IconButton,
	Input,
	SimpleGrid,
	Table,
	Tbody,
	Td,
	Text,
	Textarea,
	Th,
	Thead,
	Tr,
	useColorModeValue
} from '@chakra-ui/react';
import { format, parseISO } from 'date-fns';
import { useEffect, useState } from 'react';
import { MdContentCopy, MdRefresh } from 'react-icons/md';
import Card from '../../../components/card/Card';
import { toastService } from '../../../services/toast.service';
import {
	clearNewKey,
	fetchLiveLeadKeys,
	generateLiveLeadKey,
	revokeKey,
	saveLiveLeadWebhook
} from '../../../store/features/liveLead/liveLead.slice';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { RootState } from '../../../store/store';

const copy = (value: string, label: string) => {
	navigator.clipboard.writeText(value);
	toastService.success(`${label} copiado`);
};

/**
 * Integração do cliente: para onde entregamos (webhook + segredo) e a chave da
 * API exclusiva da feature — que é outra chave que a do Datric padrão.
 */
export default function IntegracaoCard() {
	const dispatch = useAppDispatch();
	const { config, keys, newKey, saving } = useAppSelector((state: RootState) => state.liveLead);
	const textColor = useColorModeValue('navy.700', 'white');
	const subColor = useColorModeValue('secondaryGray.700', 'secondaryGray.500');

	const [url, setUrl] = useState('');
	const [keyName, setKeyName] = useState('');
	// Headers de credencial como texto livre, uma por linha ("Nome: valor").
	// É o formato que o cliente já lê na documentação do próprio endpoint.
	const [headersText, setHeadersText] = useState('');

	useEffect(() => {
		dispatch(fetchLiveLeadKeys());
	}, [dispatch]);

	useEffect(() => {
		setUrl(config?.webhookUrl || '');
	}, [config?.webhookUrl]);

	useEffect(() => {
		const entries = Object.entries(config?.webhookHeaders || {});
		setHeadersText(entries.map(([k, v]) => `${k}: ${v}`).join('\n'));
	}, [config?.webhookHeaders]);

	// "Nome: valor" por linha → objeto. Linha sem ':' é ignorada.
	const parseHeaders = (text: string): Record<string, string> => {
		const out: Record<string, string> = {};
		for (const line of text.split('\n')) {
			const idx = line.indexOf(':');
			if (idx <= 0) continue;
			const name = line.slice(0, idx).trim();
			const value = line.slice(idx + 1).trim();
			if (name && value) out[name] = value;
		}
		return out;
	};

	return (
		<SimpleGrid columns={{ base: 1, xl: 2 }} gap='20px'>
			<Card p='20px'>
				<Text fontSize='lg' fontWeight='bold' color={textColor} mb='4px'>
					Webhook de entrega
				</Text>
				<Text fontSize='sm' color={subColor} mb='16px'>
					{(config?.batchSize ?? 1) > 1
						? `Os leads são enviados em lotes de até ${config?.batchSize} por POST (evento leads.delivered) para esta URL,`
						: 'Cada lead é enviado num POST para esta URL,'}{' '}
					{config?.delayMinutes ?? 5} minuto(s) depois de entrar na base. Respostas 2xx confirmam o
					recebimento; qualquer outra é reenviada.
				</Text>

				<FormControl mb='16px'>
					<FormLabel fontSize='sm'>URL</FormLabel>
					<Input
						size='sm'
						placeholder='https://seu-sistema.com/webhook/lead'
						value={url}
						onChange={e => setUrl(e.target.value)}
					/>
					<FormHelperText fontSize='xs'>
						Precisa ser um endereço público acessível pela internet.
					</FormHelperText>
				</FormControl>

				<FormControl mb='16px'>
					<FormLabel fontSize='sm'>Headers de autenticação</FormLabel>
					<Textarea
						size='sm'
						rows={3}
						fontFamily='mono'
						placeholder={'x-api-key: sua-chave\nAuthorization: Bearer token'}
						value={headersText}
						onChange={e => setHeadersText(e.target.value)}
					/>
					<FormHelperText fontSize='xs'>
						Um por linha, no formato <Code fontSize='xs'>Nome: valor</Code>. Preencha se o seu endpoint
						exigir credencial — sem isso ele costuma responder 401 e a entrega é reenviada até falhar.
					</FormHelperText>
				</FormControl>

				<Flex gap={2} mb='20px'>
					<Button
						size='sm'
						colorScheme='brand'
						isLoading={saving}
						onClick={() =>
							dispatch(
								saveLiveLeadWebhook({
									webhookUrl: url.trim() || null,
									webhookHeaders: parseHeaders(headersText)
								})
							)
						}
					>
						Salvar
					</Button>
					<Button
						size='sm'
						variant='outline'
						leftIcon={<MdRefresh />}
						onClick={() => dispatch(saveLiveLeadWebhook({ rotateSecret: true }))}
					>
						Renovar segredo
					</Button>
				</Flex>

				<Box>
					<Text fontSize='sm' fontWeight='bold' color={textColor} mb='6px'>
						Segredo da assinatura
					</Text>
					<Text fontSize='xs' color={subColor} mb='8px'>
						Enviamos o header <Code fontSize='xs'>x-datric-signature</Code> com o HMAC-SHA256 do corpo
						usando este segredo. Confira antes de aceitar a chamada.
					</Text>
					<Flex gap={2} align='center'>
						<Code fontSize='xs' p='8px' borderRadius='md' flex='1' overflowX='auto'>
							{config?.webhookSecret || '—'}
						</Code>
						{config?.webhookSecret ? (
							<IconButton
								aria-label='Copiar segredo'
								size='sm'
								icon={<MdContentCopy />}
								onClick={() => copy(config.webhookSecret as string, 'Segredo')}
							/>
						) : null}
					</Flex>
				</Box>
			</Card>

			<Card p='20px'>
				<Text fontSize='lg' fontWeight='bold' color={textColor} mb='4px'>
					Chave da API do Lead ao Vivo
				</Text>
				<Text fontSize='sm' color={subColor} mb='16px'>
					Chave própria desta funcionalidade (prefixo <Code fontSize='xs'>dtl_</Code>), separada da
					API de consulta do Datric. Use no header <Code fontSize='xs'>x-api-key</Code> em{' '}
					<Code fontSize='xs'>GET /live/v1/leads</Code> para reconferir o que já foi entregue.
				</Text>

				{newKey ? (
					<Alert status='success' borderRadius='md' mb='16px' flexDirection='column' alignItems='flex-start'>
						<Flex align='center' mb='6px'>
							<AlertIcon />
							<Text fontSize='sm'>Guarde agora — a chave não é exibida de novo.</Text>
						</Flex>
						<Flex gap={2} align='center' w='100%'>
							<Code fontSize='xs' p='8px' borderRadius='md' flex='1' overflowX='auto'>
								{newKey}
							</Code>
							<IconButton
								aria-label='Copiar chave'
								size='sm'
								icon={<MdContentCopy />}
								onClick={() => copy(newKey, 'Chave')}
							/>
							<Button size='sm' variant='ghost' onClick={() => dispatch(clearNewKey())}>
								Fechar
							</Button>
						</Flex>
					</Alert>
				) : null}

				<Flex gap={2} mb='16px'>
					<Input
						size='sm'
						placeholder='Nome da chave'
						value={keyName}
						onChange={e => setKeyName(e.target.value)}
					/>
					<Button
						size='sm'
						colorScheme='brand'
						onClick={() => {
							dispatch(generateLiveLeadKey(keyName.trim()));
							setKeyName('');
						}}
					>
						Gerar
					</Button>
				</Flex>

				{keys.length === 0 ? (
					<Text fontSize='sm' color={subColor}>
						Nenhuma chave criada.
					</Text>
				) : (
					<Table size='sm' variant='simple'>
						<Thead>
							<Tr>
								<Th>Nome</Th>
								<Th>Prefixo</Th>
								<Th>Último uso</Th>
								<Th></Th>
							</Tr>
						</Thead>
						<Tbody>
							{keys.map(k => (
								<Tr key={k.id}>
									<Td>{k.name}</Td>
									<Td>
										<Code fontSize='xs'>{k.prefix}</Code>
									</Td>
									<Td>{k.lastUsedAt ? format(parseISO(k.lastUsedAt), 'dd/MM/yyyy HH:mm') : '—'}</Td>
									<Td textAlign='right'>
										{k.active ? (
											<Button size='xs' colorScheme='red' variant='ghost' onClick={() => dispatch(revokeKey(k.id))}>
												Revogar
											</Button>
										) : (
											<Badge colorScheme='gray'>Revogada</Badge>
										)}
									</Td>
								</Tr>
							))}
						</Tbody>
					</Table>
				)}
			</Card>
		</SimpleGrid>
	);
}
